/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { isApiSuccess } from '../../constants/platformStatusCodes';
import { getReportStrategy, startReportStrategy } from './strategiesApi';
import normalizeStrategy from './utils/normalizeStrategy';
import { STRATEGY_STATUS } from './static/constants';

/**
 * Strategies state — one strategy per intelligence report, read and started through
 * /intel-reports/{reportId}/strategy (strategiesApi.js). There is no list endpoint, so rows only
 * exist for reports whose strategy has been fetched:
 * - `strategies`: normalized rows (utils/normalizeStrategy.js), at most one per report, looked up by
 *   report id. A report whose strategy was never started has no row.
 * - `byReport[reportId]`: the endpoint's per-report state that isn't part of a row —
 *   `{ loading, loaded, error, ready, pendingInputs }`. `loaded` is true once a GET has answered.
 */
const initialState = {
  strategies: [],
  byReport: {},
};

// Stable default for selectReportStrategyMeta, so useSelector doesn't re-render on every call.
const DEFAULT_META = Object.freeze({
  loading: false,
  loaded: false,
  error: null,
  ready: false,
  pendingInputs: Object.freeze([]),
});

const sameId = (a, b) => String(a) === String(b);

// The content is the latest COMPLETED run's, so a refresh in flight still sends the previous week;
// should a payload ever come without it, keep the body already loaded so the week stays readable.
const mergeRow = (existing, row) => ({
  ...existing,
  ...row,
  body: row.body ?? existing?.body ?? null,
});

const StrategiesSlice = createSlice({
  name: 'Strategies',
  initialState,
  reducers: {
    /** Insert or patch one report's row (already normalized); rows are matched by report id. */
    upsertStrategy(state, action) {
      const row = action.payload;
      const index = state.strategies.findIndex((existing) =>
        sameId(existing.intelReportRequestId, row.intelReportRequestId)
      );
      if (index >= 0) {
        state.strategies[index] = mergeRow(state.strategies[index], row);
      } else {
        state.strategies.unshift(row);
      }
    },
    /** Drop one report's row; the payload is the report id. */
    removeStrategy(state, action) {
      state.strategies = state.strategies.filter(
        (row) => !sameId(row.intelReportRequestId, action.payload)
      );
    },
    /** Patch one report's `byReport` entry: `{ reportId, ...fields }`. */
    setReportStrategyMeta(state, action) {
      const { reportId, ...fields } = action.payload;
      const key = String(reportId);
      state.byReport[key] = { ...DEFAULT_META, ...state.byReport[key], ...fields };
    },
  },
});

export const { upsertStrategy, removeStrategy, setReportStrategyMeta } = StrategiesSlice.actions;

const selectStrategies = (state) => state.Strategies.strategies;

/** The strategy of one intelligence report, or null. */
export const selectStrategyByReportId = (state, reportId) =>
  selectStrategies(state).find((row) => sameId(row.intelReportRequestId, reportId)) || null;

/** One report's `{ loading, loaded, error, ready, pendingInputs }` (a stable default when unknown). */
export const selectReportStrategyMeta = (state, reportId) =>
  state.Strategies.byReport[String(reportId)] || DEFAULT_META;

// The loaded intel-report row (IntelligenceReport slice), for the title and the report link.
const reportFor = (getState, reportId) =>
  getState().IntelligenceReport.reports.find((report) => sameId(report.id, reportId));

const pendingInputsOf = (data) =>
  Array.isArray(data?.pendingInputs) ? data.pendingInputs.filter(Boolean) : [];

// A GET / POST `data` → the report's meta and row: a run upserts the row, no run means the
// strategy was never started (drop any row left over).
const applyStrategyData = (dispatch, getState, reportId, data, report) => {
  dispatch(
    setReportStrategyMeta({
      reportId,
      loaded: true,
      error: null,
      ready: Boolean(data.ready),
      pendingInputs: pendingInputsOf(data),
    })
  );
  if (!data.run) {
    dispatch(removeStrategy(reportId));
    return null;
  }
  const row = normalizeStrategy(reportId, data, {
    report: report || reportFor(getState, reportId),
  });
  dispatch(upsertStrategy(row));
  return selectStrategyByReportId(getState(), reportId);
};

const payloadOf = (resp) => (resp?.data && typeof resp.data === 'object' ? resp.data : null);

const dataOf = (resp) => (isApiSuccess(resp) ? payloadOf(resp) : null);

const LOAD_ERROR = "Couldn't load the strategy.";
const START_ERROR = "We couldn't start the strategy. Please try again.";

/**
 * GET /intel-reports/{reportId}/strategy — the report's latest run and content. Used on the report
 * view's mount and by the poller while a run is in flight. A failure keeps whatever was loaded
 * before (a missed poll doesn't blank the page); `error` only blocks the page until a first load.
 * @returns {Promise<{ ok: boolean, row: object|null, error: string|null }>}
 */
export const fetchReportStrategy = (reportId) => (dispatch, getState) => {
  dispatch(setReportStrategyMeta({ reportId, loading: true }));
  return getReportStrategy(reportId)
    .then((resp) => {
      const data = dataOf(resp);
      if (!data) {
        const error = resp?.message || resp?.error || LOAD_ERROR;
        dispatch(setReportStrategyMeta({ reportId, loading: false, error }));
        return { ok: false, row: null, error };
      }
      const row = applyStrategyData(dispatch, getState, reportId, data);
      dispatch(setReportStrategyMeta({ reportId, loading: false }));
      return { ok: true, row, error: null };
    })
    .catch(() => {
      dispatch(setReportStrategyMeta({ reportId, loading: false, error: LOAD_ERROR }));
      return { ok: false, row: null, error: LOAD_ERROR };
    });
};

// 429's `message` is only "Too many requests"; its `error` says how long to wait.
const startErrorOf = (resp) =>
  resp?.httpStatus === 429
    ? resp?.error || resp?.message || START_ERROR
    : resp?.message || resp?.error || START_ERROR;

/**
 * POST /intel-reports/{reportId}/strategy — start a run: the first week, "Try again" after a
 * failure, or a refresh. An optimistic row shows straight away (GENERATING without a readable week,
 * UPDATING over one, keeping its body) and the response's run replaces it; a failure puts the
 * previous row back (or removes the optimistic one). A 409 means a run is already going: that's
 * fine, the GET brings it in.
 *
 * @param {number|string} reportId
 * @param {{ report?: object }} [options] - the normalized intel-report row, for the title
 * @returns {Promise<{ ok: boolean, row?: object|null, error?: string }>}
 */
export const startStrategy = (reportId, { report } = {}) => async (dispatch, getState) => {
  const reportRow = report || reportFor(getState, reportId);
  const previous = selectStrategyByReportId(getState(), reportId);
  const hasBody = Boolean(previous?.body);
  const base = previous || normalizeStrategy(reportId, {}, { report: reportRow });
  dispatch(
    upsertStrategy({
      ...base,
      id: base.id ?? `optimistic-${reportId}`,
      status: hasBody ? STRATEGY_STATUS.UPDATING : STRATEGY_STATUS.GENERATING,
      error: null,
    })
  );

  const failWith = (error) => {
    dispatch(removeStrategy(reportId));
    if (previous) dispatch(upsertStrategy(previous));
    return { ok: false, error };
  };

  try {
    const resp = await startReportStrategy(reportId);
    if (resp?.httpStatus === 409) {
      dispatch(fetchReportStrategy(reportId));
      return { ok: true, row: selectStrategyByReportId(getState(), reportId) };
    }
    if (!(isApiSuccess(resp) || resp?.httpStatus === 202)) return failWith(startErrorOf(resp));
    const data = payloadOf(resp);
    if (!data) {
      // Accepted without a payload: the GET brings the new run in.
      dispatch(fetchReportStrategy(reportId));
      return { ok: true, row: selectStrategyByReportId(getState(), reportId) };
    }
    const row = applyStrategyData(dispatch, getState, reportId, data, reportRow);
    return { ok: true, row };
  } catch (error) {
    return failWith(START_ERROR);
  }
};

export default StrategiesSlice.reducer;
